/*
 * Copyright (c) 2016 Omnigon Communications, LLC. All rights reserved.
 *
 * This software is the confidential and proprietary information of Omnigon Communications, LLC
 * ("Confidential Information"). You shall not disclose such Confidential Information and shall access and use it only
 * in accordance with the terms of the license agreement you entered into with Omnigon Communications, LLC, its
 * subsidiaries, affiliates or authorized licensee. Unless required by applicable law or agreed to in writing, this
 * Confidential Information is provided on an "AS IS" BASIS, WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either
 * express or implied. See the license agreement for the specific language governing permissions and limitations.
 */

import can from 'can';
import Construct from 'can/construct/construct';

export default Construct.extend(
    {
        scrollTop(topPosition) {
            can.$('html,body').animate({ scrollTop: topPosition }, '500');
        },
        getViewportSize: function() {
            var e = window,
                a = 'inner';
            if (!('innerWidth' in window)) {
                a = 'client';
                e = document.documentElement || document.body;
            }

            // Fixes CQ sim mode. Assume body explicit size can be only in pixels
            var bs = document.body.style;
            return {
                width: bs.width ? parseInt(bs.width, 10) : e[a + 'Width'],
                height: bs.height ? parseInt(bs.height, 10) : e[a + 'Height'],
            };
        },

        isOnScreen: function(element, respectTopBorder, fullyOnScreen) {
            element = element[0] || element;
            if (!(element && element instanceof Element)) {
                return false;
            }
            var parent = element;
            while (parent) {
                if (window.getComputedStyle(parent).display === 'none') {
                    return false;
                }
                parent = parent.parentElement;
            }

            var rect = element.getBoundingClientRect();
            if (respectTopBorder) {
                if (rect.bottom < 0) {
                    return false;
                }
            }
            const delta = fullyOnScreen ? rect.height : 0;
            return rect.top + delta < window.innerHeight;
        },

        pushState: function(url) {
            window.history.pushState(null, '', url);
        },

        getElementCssProperty: function($element, name, units) {
            units = units || 'px';
            var value = $element.css(name);
            var regexp = new RegExp('^(\\d+(.\\d+)?)' + units + '$', 'i');
            var matches = regexp.exec(value);
            if (matches) {
                return parseInt(matches[1], 10);
            }
            return null;
        },

        getMeta: function() {
            const raw = {};
            raw.title = can.$('title').html();
            can.$('meta').each((i, element) => {
                const $tag = can.$(element);
                const name =
                    $tag.attr('name') ||
                    $tag.attr('itemprop') ||
                    $tag.attr('property');
                if (name) {
                    raw[name] = $tag.attr('content');
                }
            });
            return raw;
        },

        getCmsTags: function() {
            const raw = this.getMeta();
            return raw['cms-tags'];
        },

        getPageContentFilterType: function() {
            if (can.$('.l-article').length > 0) {
                return 'articles';
            }
            if (can.$('.m-video-feed').length > 0) {
                return 'videos';
            }
            if (can.$('.m-photo-gallery').length > 0) {
                return 'photos';
            }
        },

        parseSocialMeta: function() {
            var raw = this.getMeta();

            var params = {
                title: '',
                description: '',
                url: '',
                network: {
                    google: {},
                    twitter: {},
                },
            };

            params.title = raw.title;
            params.description = raw.description || '';
            params.url = location.origin + location.pathname;

            if (raw['og:title']) {
                params.title = raw['og:title'];
            }
            if (raw['og:description']) {
                params.description = raw['og:description'];
            }
            if (raw['og:url']) {
                params.url = raw['og:url'];
            }

            // Reserved for future use.
            if (raw.name) {
                params.network.google.title = raw.name;
            }

            if (raw['twitter:title']) {
                params.network.twitter.title = raw['twitter:title'];
            }
            if (raw['twitter:description']) {
                params.network.twitter.description = raw['twitter:description'];
            }

            return params;
        },

        reverseChildItems: function(container) {
            var parent = container.parentNode;
            parent.removeChild(container);

            var childNodes = container.childNodes;
            var fragment = document.createDocumentFragment();
            for (var i = childNodes.length - 1; i >= 0; i--) {
                fragment.appendChild(childNodes[i]);
            }
            while (container.firstChild) {
                container.removeChild(container.firstChild);
            }
            container.appendChild(fragment);
            parent.appendChild(container);
        },

        watchElementSize: function($element, callback, interval) {
            var width = -1;
            var height = -1;
            var intervalId = setInterval(function() {
                var elementWidth = $element.width();
                var elementHeight = $element.height();
                if (width !== elementWidth || height !== elementHeight) {
                    width = elementWidth;
                    height = elementHeight;
                    callback(width, height);
                }
            }, interval);

            return function() {
                clearInterval(intervalId);
            };
        },

        addScript: function(src, callback) {
            let script = document.createElement('script'),
                loaded;

            script.setAttribute('src', src);
            if (callback) {
                script.onreadystatechange = script.onload = function() {
                    if (!loaded) {
                        callback();
                    }
                    loaded = true;
                };
            }
            document.getElementsByTagName('head')[0].appendChild(script);
        },

        disableBounceEffect: function() {
            document.ontouchmove = function(event) {
                event.preventDefault();
            };
        },
    },
    {},
);
